import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Zap } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PriorityIndicator } from '../../components/PriorityIndicator';
import { StatusBadge } from '../../components/StatusBadge';
import { api, ApiRequestError } from '../../lib/api';
import { friendlyError } from '../../lib/error_codes';

function formatDollars(cents: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatProbability(p: number | null) {
  if (p === null) return '—';
  return `${Math.round(p * 100)}%`;
}

export function DenialDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: denial, isLoading, isError, error } = useQuery({
    queryKey: ['denial', id],
    queryFn: () => api.denials.get(id!),
    enabled: Boolean(id),
  });

  const generateMutation = useMutation({
    mutationFn: () => api.appeals.generate(id!),
    onSuccess: (result) => {
      toast.success('Appeal generation started. This takes about 30 seconds.');
      qc.invalidateQueries({ queryKey: ['denial', id] });
      navigate(`/appeals/${result.id}`);
    },
    onError: (err) => {
      const code = err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR';
      toast.error(friendlyError(code));
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <LoadingSkeleton rows={8} />
      </div>
    );
  }

  if (isError || !denial) {
    return (
      <div className="p-6 text-sm text-red-600">
        {error instanceof ApiRequestError ? friendlyError(error.code) : 'Failed to load denial.'}
      </div>
    );
  }

  const canGenerateAppeal =
    denial.status === 'new' || denial.status === 'in_review';

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        {/* Back + header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate('/denials')}
              className="mb-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to worklist
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              Denial — Claim {denial.claim_id}
            </h1>
            <div className="mt-1 flex items-center gap-3">
              <StatusBadge status={denial.status} />
              <PriorityIndicator priority={denial.priority} />
            </div>
          </div>

          {canGenerateAppeal && (
            <button
              type="button"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              <Zap className="h-4 w-4" />
              {generateMutation.isPending ? 'Starting…' : 'Generate Appeal'}
            </button>
          )}
        </div>

        {/* 3-panel layout */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Panel 1: Claim Info */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Claim Information
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Payer</dt>
                <dd className="font-medium text-gray-900">{denial.payer_name ?? denial.payer_id}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Denied Amount</dt>
                <dd className="font-semibold text-gray-900">
                  {formatDollars(denial.denied_amount_cents)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">CARC Code</dt>
                <dd className="font-mono font-medium text-gray-900">{denial.carc_code}</dd>
              </div>
              {denial.rarc_codes && denial.rarc_codes.length > 0 && (
                <div>
                  <dt className="text-gray-500">RARC Codes</dt>
                  <dd className="font-mono text-gray-900">{denial.rarc_codes.join(', ')}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Category</dt>
                <dd className="capitalize text-gray-900">{denial.category}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Date of Service</dt>
                <dd className="text-gray-900">{formatDate(denial.date_of_service)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Original Charge</dt>
                <dd className="text-gray-900">
                  {denial.original_charge_cents != null
                    ? formatDollars(denial.original_charge_cents)
                    : '—'}
                </dd>
              </div>
              {denial.cpt_codes && denial.cpt_codes.length > 0 && (
                <div>
                  <dt className="text-gray-500">CPT Codes</dt>
                  <dd className="font-mono text-gray-900">{denial.cpt_codes.join(', ')}</dd>
                </div>
              )}
              {denial.payer_comments && (
                <div>
                  <dt className="text-gray-500">Payer Comments</dt>
                  <dd className="text-gray-900">{denial.payer_comments}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Panel 2: AI Analysis */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
              AI Analysis
            </h2>

            <div className="mb-4">
              <p className="mb-1 text-xs text-gray-500">Recovery Probability</p>
              <div className="flex items-end gap-2">
                <span
                  className={`text-3xl font-bold ${
                    (denial.recovery_probability ?? 0) >= 0.7
                      ? 'text-green-600'
                      : (denial.recovery_probability ?? 0) >= 0.4
                        ? 'text-amber-600'
                        : 'text-gray-500'
                  }`}
                >
                  {formatProbability(denial.recovery_probability)}
                </span>
              </div>
              {denial.recovery_probability != null && (
                <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
                  <div
                    className={`h-2 rounded-full ${
                      denial.recovery_probability >= 0.7
                        ? 'bg-green-500'
                        : denial.recovery_probability >= 0.4
                          ? 'bg-amber-500'
                          : 'bg-gray-400'
                    }`}
                    style={{ width: `${denial.recovery_probability * 100}%` }}
                  />
                </div>
              )}
            </div>

            {denial.recovery_probability_explanation && (
              <div>
                <p className="mb-1 text-xs text-gray-500">Analysis</p>
                <p className="text-sm leading-relaxed text-gray-700">
                  {denial.recovery_probability_explanation}
                </p>
              </div>
            )}
          </div>

          {/* Panel 3: Actions */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Actions
            </h2>
            <div className="space-y-2">
              {canGenerateAppeal && (
                <button
                  type="button"
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  <Zap className="h-4 w-4" />
                  Generate Appeal
                </button>
              )}
              {denial.notes && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <strong>Note:</strong> {denial.notes}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Audit history */}
        {denial.audit_history && denial.audit_history.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Audit History
            </h2>
            <ol className="space-y-2">
              {denial.audit_history.map((entry) => (
                <li key={entry.id} className="flex items-center gap-3 text-sm">
                  <span className="text-xs text-gray-400">{formatDate(entry.created_at)}</span>
                  <span className="text-gray-700">{entry.action}</span>
                  {entry.user_name && (
                    <span className="text-gray-400">by {entry.user_name}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
